import axios from 'axios';
import {
  isWeb,
  isMobileWeb,
  isAndroid,
  isIos,
  isNative,
} from '@global/screens';
import { removeAllQueues } from '@global/background';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Toast from 'react-native-toast-message';
import { authenticate } from '@/store/auth/actions';
import {
  getDataFromStorage,
  clearStorageData,
  getEnvironment,
  isVn,
  getTimezone,
  getOffset,
} from '@global/helpers';
import { VERSION } from '@constants';

const Config = getEnvironment();

let lastUsedDomain = 0;
let lastUsedDomainCart = 0;

export default function setup(dispatch) {
  // Add request interceptors
  axios.interceptors.request.use(
    async (config) => {
      if (!config.headers['SOC-PLATFORM']) {
        const isVietnam = isVn();

        const suffix = isVietnam ? '-vn' : '';

        // Platform mappings
        const platformMapping = {
          web: isMobileWeb
            ? `b2b-web-mobile${suffix}`
            : `b2b-web-desktop${suffix}`,
          android: `b2b-android${suffix}`,
          ios: `b2b-ios${suffix}`,
        };

        // Detect platform using mappings
        const platform = isAndroid
          ? platformMapping.android
          : isIos
          ? platformMapping.ios
          : platformMapping.web;

        config.headers['SOC-PLATFORM'] = platform;
      }
      // console.log('Request:', config);
      const AUTH_SKIP_URLS = ['amazonaws.com', '/app-versions/'];
      const skipAuth = AUTH_SKIP_URLS.some((p) => config.url.includes(p));
      const jwtToken = await getDataFromStorage('B2B-SOCIOLLA-TOKEN');
      if (jwtToken && !skipAuth && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${jwtToken}`;
      }

      // Append user timezone name and offset
      config.headers['User-Timezone'] = getTimezone();
      config.headers['User-Offset'] = getOffset();

      // Set Origin header if not exists
      if (isNative) {
        config.headers['Origin'] = 'b2b';
      }

      // Send semver-only version for backend observability and force-update checks
      if (isNative) {
        const semverOnly = VERSION ? VERSION.split(' ')[0] : '';
        if (semverOnly) {
          config.headers['mobapp-version'] = semverOnly;
        }
      }

      try {
        let d = new Date().getTime();
        let newGuid = '';
        let guid = await AsyncStorage.getItem('SOCIOLLA_GUID');
        if (isWeb || isMobileWeb) {
          // eslint-disable-next-line no-undef
          guid = localStorage.getItem('SOCIOLLA_GUID');
        }
        if (!guid) {
          if (
            typeof performance !== 'undefined' &&
            typeof performance.now === 'function'
          ) {
            d += performance.now(); //use high-precision timer if available
          }
          newGuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
            /[xy]/g,
            function (c) {
              const r = (d + Math.random() * 16) % 16 | 0;
              d = Math.floor(d / 16);
              return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
            },
          );
          AsyncStorage.setItem('SOCIOLLA_GUID', newGuid);
        } else {
          newGuid = await AsyncStorage.getItem('SOCIOLLA_GUID');
          if (isWeb || isMobileWeb) {
            // eslint-disable-next-line no-undef
            newGuid = localStorage.getItem('SOCIOLLA_GUID');
          }
        }

        config.headers['session_id'] = newGuid;
      } catch (e) {}

      if (config.url.startsWith(Config.MS_CATALOG_API_URL)) {
        config.url =
          Config.MS_CATALOG_API_LIST[lastUsedDomain] +
          config.url.substr(Config.MS_CATALOG_API_URL.length);
        lastUsedDomain =
          lastUsedDomain + 1 >= Config.MS_CATALOG_API_LIST.length
            ? 0
            : lastUsedDomain + 1;
      }

      if (config.url.startsWith(Config.MS_CART_API_URL)) {
        config.url =
          Config.MS_CART_API_LIST[lastUsedDomainCart] +
          config.url.substr(Config.MS_CART_API_URL.length);
        lastUsedDomainCart =
          lastUsedDomainCart + 1 >= Config.MS_CART_API_LIST.length
            ? 0
            : lastUsedDomainCart + 1;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Add response interceptors
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      try {
        if (
          'response' in error &&
          error.response &&
          error.response.status == 401
        ) {
          const req_url = error.response?.config?.url;
          const isPublicEndpoint = ['/app-versions/'].some((p) =>
            req_url?.includes(p),
          );
          if (!isPublicEndpoint) {
            dispatch(
              authenticate({
                token: '',
                userId: '',
                user: {},
              }),
            );
            await clearStorageData('B2B-SOCIOLLA-USER');
            await clearStorageData('B2B-SOCIOLLA-TOKEN');
            removeAllQueues();
            if ((req_url && req_url.indexOf('/auth/login') == -1) || !req_url) {
              Toast.show({
                text1:
                  error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'You do not have sufficient permissions.',
                topOffset: 50,
                type: 'error',
              });
            }
          }
        }
        if (
          'response' in error &&
          error.response &&
          error.response.status == 403
        ) {
          console.log(
            error.response.data && error.response.data.message
              ? error.response.data.message
              : 'You do not have sufficient permissions.',
          );
        }
      } catch (e) {}
      return Promise.reject(error);
    },
  );

  // Params-store bookkeeping that must never reach the wire:
  //  - `scoped`      — every screen's params bucket (state.params.scoped)
  //  - `refreshTick` — cache-busting counter bumped by filterAdded / paramsReseted
  // Legacy screens forward the whole params object to axios (e.g.
  // `request(JSON.parse(JSON.stringify(store.getState().params)))`), so these keys
  // ride along as query params. Strip them at the one choke point every request
  // passes through instead of at each call site.
  const INTERNAL_PARAM_KEYS = ['scoped', 'refreshTick'];

  axios.defaults.paramsSerializer = (params = {}) => {
    const parts = [];

    const isPlainObject = (val) =>
      Object.prototype.toString.call(val) === '[object Object]';

    const isJsonString = (val) => {
      if (typeof val !== 'string') {
        return false;
      }

      const first = val[0];
      const last = val[val.length - 1];

      if ((first === '{' && last === '}') || (first === '[' && last === ']')) {
        try {
          JSON.parse(val);
          return true;
        } catch {
          return false;
        }
      }

      return false;
    };

    const normalize = (value) => {
      if (value === undefined) {
        return undefined;
      }

      if (value === null) {
        return 'null';
      }

      if (Array.isArray(value) || isPlainObject(value)) {
        return JSON.stringify(value);
      }

      if (isJsonString(value)) {
        return value;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }

      return value;
    };

    Object.keys(params).forEach((key) => {
      if (INTERNAL_PARAM_KEYS.includes(key)) {
        return;
      }
      const value = normalize(params[key]);
      if (value === undefined) {
        return;
      }

      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    });

    return parts.join('&');
  };
}
